import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Plus, ChevronRight } from 'lucide-react';
import { ProvisionWorkspaceModal } from './ProvisionWorkspaceModal';
import { PageBody } from '@/components/layout/AppShell';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { StatusDot } from '@/components/ui/StatusDot';
import { useTenant } from './context';
import { useTenantPlan } from '@/api/tenants';
import { REGION_LABEL, tierLabel } from '@/lib/tier';
import { relativeTime } from '@/lib/format';
import { ProvisioningProgress } from '@/components/provisioning/ProvisioningProgress';

export function TenantWorkspaces() {
  const { workspaces, tenant } = useTenant();
  const [provisionOpen, setProvisionOpen] = useState(false);
  const inFlight = workspaces.filter(w => w.status === 'provisioning' || w.status === 'error');

  // Effective limits come from the backend (plan registry, MC-611); while
  // loading, don't block the button — the backend enforces regardless.
  const plan = useTenantPlan(tenant.slug);
  const maxInstances = plan.data?.limits.max_instances ?? null;
  const planName = plan.data?.plans.find(p => p.slug === plan.data!.tier)?.name ?? tierLabel(tenant.tier);
  const atLimit = maxInstances !== null && workspaces.length >= maxInstances;
  const planLabel = !plan.data
    ? `${planName} plan`
    : maxInstances === null
      ? `${planName} plan · unlimited instances`
      : `${planName} plan · ${workspaces.length} of ${maxInstances} instance${maxInstances === 1 ? '' : 's'} used`;

  return (
    <PageBody>
      {/* Live steppers for anything mid-provision — this is where the
          "Watch progress" toast link lands. */}
      {inFlight.map(w => (
        <div key={w.id} className="mb-4">
          <ProvisioningProgress tenantId={tenant.slug} workspaceId={w.id} status={w.status} />
        </div>
      ))}

      <Card>
        <CardHeader
          title={`${workspaces.length} instance${workspaces.length === 1 ? '' : 's'}`}
          description={`Each instance is a fully separate NousViz deployment — its own server, database and configuration. ${planLabel}.`}
          actions={
            <Button
              variant="secondary" size="sm" iconLeft={<Plus size={13} />}
              onClick={() => setProvisionOpen(true)}
              disabled={atLimit}
              title={atLimit
                ? `The ${planName} plan allows ${maxInstances} instance${maxInstances === 1 ? '' : 's'} — upgrade the plan in Settings to add more`
                : 'Create a new NousViz instance for this customer (~10 minutes)'}
            >
              Create instance
            </Button>
          }
        />
        {workspaces.length > 0 ? (
          <div className="overflow-x-auto">
            <Table className="min-w-[820px]">
              <THead>
                <tr>
                  <TH>Instance</TH>
                  <TH>Region</TH>
                  <TH>Version</TH>
                  <TH>Last health check</TH>
                  <TH className="w-8"></TH>
                </tr>
              </THead>
              <TBody>
                {workspaces.map(w => (
                  <TR key={w.id} className="cursor-pointer group">
                    <TD>
                      <Link
                        to={`/tenants/${tenant.slug}/workspaces/${w.id}`}
                        className="flex items-center gap-2.5 group"
                      >
                        <StatusDot status={w.status} />
                        <div>
                          <div className="font-medium text-fg group-hover:text-accent">{w.name}</div>
                          <div className="text-2xs text-fg-subtle">{w.url.replace(/^https?:\/\//, '')}</div>
                        </div>
                      </Link>
                    </TD>
                    <TD className="text-fg-muted">{REGION_LABEL[w.region]}</TD>
                    <TD>
                      <span className="font-mono text-xs text-fg-muted">{w.version || '—'}</span>
                      {w.version && w.latestVersion && w.version !== w.latestVersion && (
                        <Badge tone="warn" className="ml-1.5">Drift</Badge>
                      )}
                    </TD>
                    <TD className="text-fg-muted">{relativeTime(w.lastActiveAt)}</TD>
                    <TD>
                      <div className="flex items-center gap-1">
                        <a
                          href={w.url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-fg-subtle hover:text-accent"
                          aria-label="Open app"
                        >
                          <ExternalLink size={13} />
                        </a>
                        <Link
                          to={`/tenants/${tenant.slug}/workspaces/${w.id}`}
                          className="text-fg-subtle group-hover:text-accent"
                          aria-label="Open workspace"
                        >
                          <ChevronRight size={14} />
                        </Link>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        ) : (
          <div className="px-5 py-8 text-center text-sm text-fg-muted">
            {tenant.status === 'churned' ? 'Customer has been deprovisioned.' : 'No instances yet — use Create instance to spin one up.'}
          </div>
        )}
      </Card>
      <ProvisionWorkspaceModal open={provisionOpen} onClose={() => setProvisionOpen(false)} tenant={tenant} />
    </PageBody>
  );
}
